<template>
  <transition name="t-app-overlay">
    <div v-show="active" class="app-overlay">
      <!-- ban -->
      <v-container v-if="userBan" fill-height>
        <v-row align="center" justify="center" class="flex-column">
          <fai :icon="['fad', 'ban']" class="primary500--text text-h3 mb-3" />
          <h4 class="font-weight-bold text-h6 primary500--text text-uppercase mb-4">
            Account banned
          </h4>
          <div class="text-body-2 text--secondary ban-info">
            <p>
              This account is disabled and can no longer be used.<br>
              Log out below to access the website as a guest.
            </p>

            <h4 class="primary400--text text-uppercase text-body-2 font-weight-bold">
              Ban Reason
            </h4>
            <p class="text-body-1 primary100--text" v-text="userBan.reason" />

            <h4 class="primary400--text text-uppercase text-body-2 font-weight-bold">
              Expiration
            </h4>
            <p class="text-body-1 primary100--text">
              {{ userBan.expiration | relativeTime }}
            </p>
          </div>

          <v-btn
            color="primary700 primary200--text"
            small depressed :ripple="false"
            class="rounded-0 font-weight-semibold"
            @click="$emit('logout')"
          >
            <fai :icon="['fad', 'sign-out']" class="mr-2" />
            Log out
          </v-btn>
        </v-row>
      </v-container>

      <!-- staff only -->
      <v-container v-else-if="staffOnly" fill-height>
        <v-row align="center" justify="center" class="flex-column">
          <fai :icon="['fad', 'tools']" class="primary500--text text-h3 mb-3" />
          <h4 class="font-weight-bold text-h6 primary400--text text-uppercase mb-2">
            Maintenance
          </h4>
          <p class="mb-0" v-text="conga.maintenance.info || 'We will be back with you shortly.'" />
          <fai
            :icon="['fas', 'sign-in-alt']"
            class="link mt-2 grey400--text"
            @click="$modal.open('auth', { maxWidth: 320, persistent: true })"
          />
        </v-row>
      </v-container>

      <!-- shutdown -->
      <v-container v-else-if="shuttingDown" fill-height>
        <v-row align="center" justify="center" class="flex-column">
          <fai :icon="['fad', 'cog']" spin class="blue400--text text-h3 mb-3" />
          <h4 class="font-weight-bold text-h6 blue400--text text-uppercase mb-2">
            Updating...
          </h4>
          <p class="mb-0 blue100--text text-body-2">
            The website is restarting to apply an update, please wait a few seconds.
          </p>
        </v-row>
      </v-container>

      <!-- TODO move to own component -->
      <div v-else class="loading-gradient d-flex text-uppercase">
        <div class="loading-text py-8 px-8 text-right flex-grow-1">
          <h2 class="text-h6 text-sm-h2 lh-1 text--secondary font-weight-bold">
            Loading
          </h2>
          <h4 class="text-body-1 text-sm-h6 lh-1 grey300--text font-weight-bold">
            {{ currentText }}
          </h4>
        </div>
        <div class="d-none d-sm-flex align-center justify-center primary700 px-10 link" @click="angry()">
          <span class="font-weight-semibold lh-1 text-h6 primary400--text">Cancel</span>
        </div>
      </div>

      <!-- logo -->
      <v-img src="@/assets/img/logo.png" class="logo rounded-0" width="250" />
    </div>
  </transition>
</template>

<script>
const TEXTS = [
  'Rock-fighting newspawns',
  'Removing C4 from airdrops',
  'Door-camping a solo',
  'Leaving the lanterns on',
  'Building a 2x2',
  'Offlining your base',
  'Drinking all your water',
  'Loading the coolest rust site',
  'Picking your hemp',
  'Building roleplayer hut',
  'Taking Heli',
  'Bowing a full gear',
  'Spear-raiding your neighbour',
  'Burning your food',
  'Flameraiding a raided base',
  'Crafting satchels',
  'Stealing from furnace',
  'Sniping nakeds',
  'Taking oilrig',
  'Filling furnaces',
]

export default {
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    staffOnly: {
      type: Boolean,
      default: false,
    },
    shuttingDown: {
      type: Boolean,
      default: false,
    },
    conga: {
      type: Object,
      default: null,
    },
    userBan: {
      type: [Object, Boolean],
      default: false,
    },
  },
  data: () => ({
    interval: null,
    currentTextIndex: Math.floor(Math.random() * TEXTS.length),
    currentText: null,
  }),
  watch: {
    active: {
      handler(val) {
        if (val) {
          this.pickText()
          this.interval = setInterval(this.pickText, 1800)
        } else {
          clearInterval(this.interval)
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    clearInterval(this.interval)
  },
  methods: {
    pickText() {
      this.currentText = TEXTS[this.currentTextIndex]

      if (this.currentTextIndex >= TEXTS.length - 1) {
        this.currentTextIndex = 0
      } else {
        this.currentTextIndex += 1
      }
    },
    angry() {
      clearInterval(this.interval)

      this.currentText = 'Adding time to loading screen'

      this.$emit('add-time', 3000)

      this.interval = setInterval(this.pickText, 1800)
    },
  },
}
</script>

<style lang="scss" scoped>
$color: desaturate(grey(500), 5%);

.app-overlay {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: grey(600);
  transition: opacity 0.5s ease;

  &::before {
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.5;
    background: url('../../assets/img/grain.png') repeat;
    content: ' ';
  }

  > .container {
    position: relative;
    z-index: 1;
  }
}

.loading-gradient {
  position: fixed;
  top: 50%;
  left: 0;
  width: 50%;
  min-width: 400px;
  background: linear-gradient(to left, $color, rgba($color, 0));
  transform: translate(25%, -50%);
}

.logo {
  position: fixed;
  bottom: 8%;
  left: 50%;
  transform: translateX(-50%);
}

.ban-info {
  max-width: 450px;
}

.t-app-overlay {
  &-enter,
  &-leave-to, {
    opacity: 0;
  }

  &-enter-to, {
    opacity: 1;
  }
}
</style>
